import { useMemo, useState } from "react";
import { Search, ArrowUp, ArrowDown, X, Table as TableIcon } from "lucide-react";

const PAGE_SIZE = 50;

export default function DataDrawer({ open, onClose, dealers, outliers }) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState({ key: "dist", dir: "desc" });
  const [page, setPage] = useState(0);
  const [showOnlyOutliers, setShowOnlyOutliers] = useState(false);

  // Flatten rows: inliers + outliers with a flag
  const rows = useMemo(() => {
    const out = [];
    for (const d of dealers) {
      for (const p of d.points) {
        out.push({
          sap: p.sap,
          dealer: d.name,
          lat: p.lat,
          lng: p.lng,
          dist: p.dist,
          outlier: false,
          fill: d.fill,
        });
      }
    }
    // Outliers come from separate GeoJSON
    for (const f of outliers?.features || []) {
      out.push({
        sap: f.properties.sap,
        dealer: f.properties.dealer,
        lat: f.geometry.coordinates[1],
        lng: f.geometry.coordinates[0],
        dist: f.properties.dist,
        outlier: true,
        fill: "#ea002a",
      });
    }
    return out;
  }, [dealers, outliers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    let r = rows;
    if (showOnlyOutliers) r = r.filter(x => x.outlier);
    if (q) r = r.filter(x =>
      x.sap.toLowerCase().includes(q) ||
      x.dealer.toLocaleLowerCase("tr").includes(q)
    );
    return r;
  }, [rows, search, showOnlyOutliers]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, dir } = sortBy;
    arr.sort((a, b) => {
      let av = a[key], bv = b[key];
      if (typeof av === "string") { av = av.toLocaleLowerCase("tr"); bv = bv.toLocaleLowerCase("tr"); }
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortBy]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const toggleSort = (key) => {
    setSortBy(prev => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
    setPage(0);
  };

  const SortHeader = ({ label, k, className = "" }) => (
    <th
      onClick={() => toggleSort(k)}
      className={`px-2 py-2 text-left cursor-pointer hover:bg-white/5 select-none ${className}`}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortBy.key === k && (sortBy.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
      </div>
    </th>
  );

  if (!open) return null;

  return (
    <div
      data-testid="data-drawer"
      className="absolute left-0 right-0 bottom-0 z-[1500] bg-[#0B0F17] border-t border-white/10 flex flex-col"
      style={{ height: "42vh" }}
    >
      <div className="flex-shrink-0 h-11 px-3 flex items-center gap-3 border-b border-white/10 bg-[#111726]">
        <div className="flex items-center gap-2">
          <TableIcon size={14} className="text-blue-400" />
          <span className="font-display font-bold text-sm">Ham Veri</span>
          <span className="text-[11px] text-slate-500 font-mono">
            {sorted.length.toLocaleString("tr")} / {rows.length.toLocaleString("tr")} kayıt
          </span>
        </div>
        <div className="relative flex-1 max-w-md ml-4">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="data-drawer-search"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder="SAP kodu veya bayi adı ara..."
            className="w-full bg-[#0B0F17] border border-white/10 rounded pl-7 pr-2 py-1 text-[12px] focus:outline-none focus:border-blue-400"
          />
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer">
          <input
            data-testid="data-drawer-only-outliers"
            type="checkbox"
            checked={showOnlyOutliers}
            onChange={e => { setShowOnlyOutliers(e.target.checked); setPage(0); }}
            className="accent-red-500"
          />
          Sadece outlier
        </label>
        <button
          data-testid="data-drawer-close"
          onClick={onClose}
          className="ml-auto p-1 rounded hover:bg-white/10 text-slate-400"
        >
          <X size={14} />
        </button>
      </div>

      <div className="flex-1 overflow-auto custom-scroll">
        <table className="w-full text-[12px] font-mono">
          <thead className="sticky top-0 bg-[#111726] text-[10px] uppercase tracking-wider text-slate-400 border-b border-white/10">
            <tr>
              <th className="px-2 py-2 w-8"></th>
              <SortHeader label="SAP Kodu" k="sap" />
              <SortHeader label="Bayi Adı" k="dealer" />
              <SortHeader label="Enlem (x)" k="lat" />
              <SortHeader label="Boylam (y)" k="lng" />
              <SortHeader label="Merkez Uzaklığı" k="dist" />
              <SortHeader label="Durum" k="outlier" />
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={`${r.sap}-${i}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="px-2 py-1.5">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: r.fill }} />
                </td>
                <td className="px-2 py-1.5 text-slate-100">{r.sap}</td>
                <td className="px-2 py-1.5 text-slate-300 font-sans">{r.dealer}</td>
                <td className="px-2 py-1.5 text-slate-400 tabular-nums">{r.lat.toFixed(5)}</td>
                <td className="px-2 py-1.5 text-slate-400 tabular-nums">{r.lng.toFixed(5)}</td>
                <td className="px-2 py-1.5 text-slate-400 tabular-nums">{r.dist.toFixed(4)}°</td>
                <td className="px-2 py-1.5">
                  {r.outlier ? (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 uppercase">Outlier</span>
                  ) : (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 uppercase">İç</span>
                  )}
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-slate-500 text-xs">Kayıt bulunamadı</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex-shrink-0 h-9 px-3 flex items-center justify-between border-t border-white/10 bg-[#111726] text-[11px] text-slate-400">
        <div>Sayfa <span className="text-slate-100 font-bold">{page + 1}</span> / {pageCount}</div>
        <div className="flex gap-1">
          <button
            disabled={page === 0}
            onClick={() => setPage(0)}
            className="px-2 py-0.5 rounded hover:bg-white/10 disabled:opacity-30"
          >İlk</button>
          <button
            disabled={page === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            className="px-2 py-0.5 rounded hover:bg-white/10 disabled:opacity-30"
          >‹ Önceki</button>
          <button
            disabled={page >= pageCount - 1}
            onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
            className="px-2 py-0.5 rounded hover:bg-white/10 disabled:opacity-30"
          >Sonraki ›</button>
          <button
            disabled={page >= pageCount - 1}
            onClick={() => setPage(pageCount - 1)}
            className="px-2 py-0.5 rounded hover:bg-white/10 disabled:opacity-30"
          >Son</button>
        </div>
      </div>
    </div>
  );
}
